import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Premium searchable single-select combobox.
 * - Keyboard-navigable, filter-as-you-type, no external dep.
 * - Uses data-testid={testid} on the trigger button.
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  testid,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.toLowerCase().includes(term));
  }, [q, options]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
    setQ('');
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid={testid}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between text-left border-b px-1 py-3 text-[16px] tracking-tight transition-colors ${
          open ? 'border-[#C7A36A]' : 'border-black/15 hover:border-black/40'
        }`}
      >
        <span className={value ? 'text-[#111]' : 'text-black/40'}>{value || placeholder}</span>
        <span className="text-black/40 text-[12px]">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-black/10 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)]">
          <input
            ref={inputRef}
            data-testid={testid ? `${testid}-search` : undefined}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            placeholder="Search…"
            className="w-full border-b border-black/10 px-4 py-3 text-[14px] focus:outline-none"
          />
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-4 py-4 text-[13px] text-black/50">No matches</div>
            )}
            {filtered.map((o, i) => (
              <button
                key={o}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
                data-testid={testid ? `${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined}
                className={`w-full text-left px-4 py-2.5 text-[14px] transition-colors ${
                  active === i ? 'bg-[#F5F5F5] text-[#111]' : 'text-black/80 hover:bg-[#F5F5F5]'
                }`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
